import { useState } from 'react';

/**
 * Inline validation: a field's error shows once it was left (blur) or a submit was tried. `attempt()` reveals every
 * error and says whether the form may be sent.
 *
 * `onlyEdited`: leaving a field counts only once it was changed (`edit(k)`), so tabbing or clicking through an
 * untouched form shows nothing red; an empty required field speaks up at submit.
 */
export function useFieldErrors<K extends string>(errors: Record<K, string | null>, opts: { onlyEdited?: boolean } = {}) {
  const [touched, setTouched] = useState<Partial<Record<K, boolean>>>({});
  const [edited, setEdited] = useState<Partial<Record<K, boolean>>>({});
  const [tried, setTried] = useState(false);
  return {
    show: (k: K) => (tried || touched[k] ? errors[k] : null),
    blur: (k: K) => () => {
      if (opts.onlyEdited && !edited[k]) return;
      setTouched((t) => (t[k] ? t : { ...t, [k]: true }));
    },
    /** the field's value changed (needed for `blur` to count with `onlyEdited`) */
    edit: (k: K) => setEdited((e) => (e[k] ? e : { ...e, [k]: true })),
    /** show this field's error now (a value set in one step: a pin dropped, a point pasted) */
    touch: (k: K) => setTouched((t) => (t[k] ? t : { ...t, [k]: true })),
    attempt: () => {
      setTried(true);
      return !Object.values(errors).some(Boolean);
    },
    reset: () => {
      setTried(false);
      setTouched({});
      setEdited({});
    },
    isTouched: (k: K) => !!touched[k],
  };
}
